import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Select, cn } from '@chapche/ui';
import { CloseIcon } from '../appearence/icons/close_icon';
import { useTranslation } from 'react-i18next';
import { MOUSE_DOWN_EVENT, SELECT_WITH_SEARCH_LIMIT } from '../constant';

const toOptions = (list) => (list || []).map((item) => ({ value: item.id, label: item.name }));

const SelectWithoutTokens = ({ listOfOptions = [], handleChangeValue, titleValue, withCleanIcon }) => {
    const wrapperRef = useRef(null);
    const [optionListIsOpen, setOptionListIsOpen] = useState(false);
    const [activeOption, setActiveOption] = useState(titleValue);
    const [searchInputValue, setSearchInputValue] = useState(titleValue || '');
    const { i18n } = useTranslation();
    const withSearch = listOfOptions.length > SELECT_WITH_SEARCH_LIMIT;

    const handleClickOutside = useCallback((event) => {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
            setOptionListIsOpen(false);
        }
    }, []);

    useEffect(() => {
        setActiveOption(titleValue);
        setSearchInputValue(titleValue || '');
    }, [titleValue]);

    useEffect(() => {
        document.addEventListener(MOUSE_DOWN_EVENT, handleClickOutside);
        return () => {
            document.removeEventListener(MOUSE_DOWN_EVENT, handleClickOutside);
        };
    }, [handleClickOutside]);

    const applyItem = (item) => {
        if (!item) {
            return;
        }
        handleChangeValue(item);
        setActiveOption(item.name);
        setSearchInputValue(item.name);
    };

    const clearItemValue = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setActiveOption('');
        setSearchInputValue('');
        handleChangeValue({ value: '' });
    };

    const selected = listOfOptions.find((item) => item.name === activeOption)
        || listOfOptions.find((item) => String(item.id) === String(activeOption));

    const filteredOptions = searchInputValue
        ? listOfOptions.filter((item) => item.name.toLowerCase().includes(searchInputValue.toLowerCase()))
        : listOfOptions;

    if (!withSearch) {
        return (
            <div className="relative w-full">
                <Select
                    fullWidth
                    className="mt-0"
                    options={toOptions(listOfOptions)}
                    value={selected?.id}
                    placeholder={i18n.t('app.page.preference.selectFromList')}
                    onChange={(value) => {
                        applyItem(listOfOptions.find((item) => String(item.id) === String(value)));
                    }}
                />
                {withCleanIcon && activeOption ? (
                    <span
                        className="absolute top-1/2 right-40 -translate-y-1/2 cursor-pointer"
                        onClick={clearItemValue}
                    >
                        <CloseIcon nameOfClass="select__choice-cleaning" />
                    </span>
                ) : null}
            </div>
        );
    }

    return (
        <div
            className={cn(
                'select__choice relative mt-0 box-border flex min-h-[46px] w-full max-w-none cursor-pointer items-center rounded-input border border-solid border-light-grey bg-white px-16 py-12 text-dark-green',
                optionListIsOpen && 'select__choice_active',
            )}
            onClick={() => setOptionListIsOpen(true)}
            ref={wrapperRef}
        >
            {optionListIsOpen ? (
                <input
                    className="block__content select__choice-input m-0 min-w-0 flex-1 border-0 bg-transparent p-0 font-montserrat-medium text-l text-ink outline-none placeholder:text-secondary-light-grey"
                    onChange={(event) => setSearchInputValue(event.target.value)}
                    onClick={(event) => event.stopPropagation()}
                    value={searchInputValue}
                />
            ) : (
                <p className={cn('m-0 min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap', !activeOption && 'select__choice-placeholder text-secondary-light-grey')}>
                    {activeOption || i18n.t('app.page.preference.selectFromList')}
                </p>
            )}
            {withCleanIcon && activeOption ? (
                <span onClick={clearItemValue}>
                    <CloseIcon nameOfClass="select__choice-cleaning" />
                </span>
            ) : null}
            <span
                className={cn(
                    'select__choice-icon ml-auto h-20 w-20 shrink-0 text-dark-green',
                    optionListIsOpen ? 'select__choice-icon_active rotate-0' : 'rotate-180',
                )}
                aria-hidden
            >
                ▾
            </span>
            {optionListIsOpen && filteredOptions.length ? (
                <div className="select__choice-dropdown absolute top-full left-0 z-20 mt-8 max-h-[200px] w-full overflow-y-auto rounded-input border border-solid border-light-grey bg-white">
                    {filteredOptions.map((item, index) => (
                        <div
                            key={item.id + index}
                            className="block__content select__choice-option cursor-pointer px-16 py-12 font-montserrat hover:text-list-hover"
                            onClick={(event) => {
                                event.stopPropagation();
                                setOptionListIsOpen(false);
                                applyItem(item);
                            }}
                        >
                            {item.name}
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

export default SelectWithoutTokens;
